import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import {
  colors,
  createDiagonalPattern,
  defaultXAxisGridConfig,
  legendLabelsConfig,
  numberToCurrency,
} from './utils';

export const createCumulativeAnnualCashflowsChart = (graphData, scope) => {
  const chartCanvas = document.getElementById('cumulativeAnnualCashFlowChartCanvas');
  chartCanvas.parentElement.style.display = '';

  if (!graphData[0] || !chartCanvas) {
    console.log('no data returned or canvas found');
    return;
  }

  const chartLoader = document.getElementById('cumulativeChartLoader');
  chartLoader.classList.remove('lds-dual-ring');

  if (!graphData[0].annualData || graphData[0].annualData.length === 0) {
    console.log('no annual, cumulative cash flow yet for this fund');
    chartCanvas.parentElement.style.display = 'none';
    return;
  }

  const sortedCashflows = graphData[0].annualData.sort((a, b) => a.year - b.year);
  if (!hasNonZeroDataForThisScope(sortedCashflows, scope)) {
    chartCanvas.parentElement.style.display = 'none';
    return;
  }

  const tooltipBody = (context) => {
    const {
      dataset: { label },
      parsed: { y: rawValue },
    } = context;

    return ` ${label} : ${numberToCurrency(rawValue, scope)}`;
  };

  const minYAxisValue = (() => {
    const { annualData } = graphData[0];
    const minCall = Math.min(
      ...annualData.map(
        (y) =>
          Number(y.real[scope].cumulativeCalled) + Number(y.provisional[scope].cumulativeCalled)
      )
    );
    return Math.ceil(minCall * 1.6);
  })()

  const maxYAxisValue = (() => {
    const { annualData } = graphData[0];
    const maxCall = Math.max(
      ...annualData.map(
        (y) =>
          Number(y.real[scope].cumulativeDistributed) + Number(y.provisional[scope].cumulativeDistributed)
      )
    );
    return Math.ceil(maxCall * 1.6);
  })()

  let responsiveRatio = 1.3;

  switch (true) {
    case (window.innerWidth > 576 && window.innerWidth < 1200):
      responsiveRatio = 2;
      break;
    case window.innerWidth >= 1200:
      responsiveRatio = 2.5;
      break;
    default:
      responsiveRatio = 1.3;
  }

  const buildDatasets = (() => {
    let numberOfYears = graphData[0].annualData.length;
    let lastcumulativeDistributedWaitingToBePaid =  graphData[0]
      .annualData[numberOfYears - 1]
      .real
      .individual
      .cumulativeDistributedWaitingToBePaid;

    if (scope === 'individual' && lastcumulativeDistributedWaitingToBePaid > 0) {
      return [
        {
          label: 'Appels',
          stack: 'callings',
          order: 1,
          backgroundColor: colors.calls,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.cumulativeCalled`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            display: false,
          },
        },
        {
          label: 'Appels prévisionnels',
          stack: 'callings',
          order: 4,
          backgroundColor: createDiagonalPattern(colors.calls),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.cumulativeCalled`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            formatter: function (value) {
              const { cumulativeCalled: realCumulativeCalledAmount } = value.real[scope];
              const { cumulativeCalled: provisionalCumulativeCalledAmount } = value.provisional[scope];

              const result = numberToCurrency(+realCumulativeCalledAmount + +provisionalCumulativeCalledAmount, scope);
              return result;
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { cumulativeCalled: realCumulativeCalledAmount } = value.real[scope];
              const { cumulativeCalled: provisionalCumulativeCalledAmount } = value.provisional[scope];
              const dataDisplayed = !context?.chart?.legend?.legendItems[0]?.hidden;

              return dataDisplayed && +realCumulativeCalledAmount + +provisionalCumulativeCalledAmount < 0;
            },
          },
        },
        {
          label: 'Distributions',
          stack: 'distributions',
          order: 2,
          backgroundColor: colors.distributions,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.cumulativeDistributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            display: function (context) {
              return false;
            },
          },
        },
        {
          label: 'Distributions en attente de versement',
          stack: 'distributions',
          order: 3,
          backgroundColor: colors.distributionsWaitingToBePaid,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.cumulativeDistributedWaitingToBePaid`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            display: function (context) {
              return false;
            },
          },
        },
        {
          label: 'Distributions prévisionnelles',
          stack: 'distributions',
          order: 4,
          backgroundColor: createDiagonalPattern(colors.distributions),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.cumulativeDistributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            formatter: function (value) {
              const { cumulativeDistributed: realDistributedAmount } = value.real[scope];
              const { cumulativeDistributed: provisionalDistributedAmount } = value.provisional[scope];
              const {
                cumulativeDistributedWaitingToBePaid: realCumulativeDistributedWaitingToBePaid,
              } = value.real[scope];

              return numberToCurrency(
                +realDistributedAmount + +provisionalDistributedAmount + +realCumulativeDistributedWaitingToBePaid,
                scope,
              );
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { cumulativeDistributed: realAmount } = value.real[scope];
              const { cumulativeDistributed: provisionalAmount } = value.provisional[scope];
              const { cumulativeDistributedWaitingToBePaid: realwaitingToBePaidAmount } = value.real[scope];

              const dataDisplayed = !context?.chart?.legend?.legendItems[1]?.hidden;

              return dataDisplayed && +realAmount + +provisionalAmount + +realwaitingToBePaidAmount > 0;
            },
          },
        },
      ];
    } else {
      return [
        {
          label: 'Appels',
          stack: 'callings',
          order: 1,
          backgroundColor: colors.calls,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.cumulativeCalled`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            display: false,
          },
        },
        {
          label: 'Appels prévisionnels',
          stack: 'callings',
          order: 4,
          backgroundColor: createDiagonalPattern(colors.calls),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.cumulativeCalled`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            formatter: function (value) {
              const { cumulativeCalled: realCumulativeCalledAmount } = value.real[scope];
              const { cumulativeCalled: provisionalCumulativeCalledAmount } = value.provisional[scope];

              const result = numberToCurrency(
                +realCumulativeCalledAmount + +provisionalCumulativeCalledAmount,
                scope,
              );
              return result;
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { cumulativeCalled: realCumulativeCalledAmount } = value.real[scope];
              const { cumulativeCalled: provisionalCumulativeCalledAmount } = value.provisional[scope];
              const dataDisplayed = !context?.chart?.legend?.legendItems[0]?.hidden;

              return dataDisplayed && +realCumulativeCalledAmount + +provisionalCumulativeCalledAmount < 0;
            },
          },
        },
        {
          label: 'Distributions',
          stack: 'distributions',
          order: 2,
          backgroundColor: colors.distributions,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.cumulativeDistributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            display: function (context) {
              return false;
            },
          },
        },
        {
          label: 'Distributions prévisionnelles',
          stack: 'distributions',
          order: 4,
          backgroundColor: createDiagonalPattern(colors.distributions),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.cumulativeDistributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            formatter: function (value) {
              const { cumulativeDistributed: realDistributedAmount } = value.real[scope];
              const { cumulativeDistributed: provisionalDistributedAmount } = value.provisional[scope];

              return numberToCurrency(+realDistributedAmount + +provisionalDistributedAmount, scope);
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { cumulativeDistributed: realAmount } = value.real[scope];
              const { cumulativeDistributed: provisionalAmount } = value.provisional[scope];

              const dataDisplayed = !context?.chart?.legend?.legendItems[1]?.hidden;

              return dataDisplayed && +realAmount + +provisionalAmount > 0;
            },
          },
        }
      ]
    }
  })

  const config = {
    type: 'bar',
    data: {
      labels: sortedCashflows.map((cashflow) => cashflow.year),
      datasets: buildDatasets(),
    },
    options: {
      devicePixelRatio: 1.5,
      aspectRatio: responsiveRatio,
      categoryPercentage: 0.5,
      barPercentage: 1,
      responsive: true,
      maxBarThickness: 100,
      scales: {
        x: {
          position: { y: 0 },
          ticks: {
            align: 'center',
            z: 10,
          },
          grid: defaultXAxisGridConfig,
        },
        y: {
          stacked: true,
          ticks: {
            display: false,
          },
          grid: {
            drawBorder: false,
            color: 'rgba(51,205,153,0.15)',
          },
          suggestedMin: -Math.abs(minYAxisValue),
          suggestedMax: Math.abs(maxYAxisValue),
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'center',
          labels: legendLabelsConfig,
        },
        tooltip: {
          callbacks: {
            label: tooltipBody,
          },
        },
      },
    },
  };

  return new Chart(chartCanvas, {
    plugins: [ChartDataLabels],
    ...config,
  });
};

function hasNonZeroDataForThisScope(annual_data, scope) {
  return annual_data.some((year_object) => {
    const hasNonZeroDataInReal = Object.values(year_object?.real[scope]).some(value => value !== 0);
    const hasNonZeroDataInProvisional = Object.values(year_object?.provisional[scope]).some(value => value !== 0);

    return hasNonZeroDataInReal || hasNonZeroDataInProvisional;
  })
}
